import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { tournamentApi } from '../services/api'
import SplitCountdown from '../components/SplitCountdown'

/**
 * Public "pay your share" page for a CTQ split payment. Reached from the emailed
 * link /pay-share/:token — the golfer's unguessable MagicToken is the credential
 * (no login). Shows the trip summary (package total only — never hotel prices),
 * this golfer's share + their future schedule, and a card form to authorize it.
 * The trip books only once every golfer has paid.
 *
 * CTQ feature branch — not deployed to production until the feature is done.
 */
const money = (n, cur = 'CAD') =>
  n == null ? '—' : new Intl.NumberFormat('en-CA', { style: 'currency', currency: cur }).format(Number(n))
function fmtFullDate(v, lang) {
  if (!v) return ''
  const ymd = String(v).slice(0, 10)
  try { return new Date(ymd + 'T00:00:00Z').toLocaleDateString(lang === 'fr' ? 'fr-CA' : 'en-CA', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric', timeZone: 'UTC' }) } catch { return ymd }
}
function fmtDay(v) {
  if (!v) return ''
  const ymd = String(v).slice(0, 10)
  try { return new Date(ymd + 'T00:00:00Z').toLocaleDateString('en-CA', { weekday: 'short', month: 'short', day: 'numeric', timeZone: 'UTC' }) } catch { return ymd }
}
function contrastText(hex) {
  const m = String(hex || '').replace('#', '')
  const r = parseInt(m.slice(0, 2), 16), g = parseInt(m.slice(2, 4), 16), b = parseInt(m.slice(4, 6), 16)
  if ([r, g, b].some(Number.isNaN)) return '#fff'
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255 > 0.6 ? '#0f172a' : '#fff'
}
function fmtClock(s) {
  if (s == null) return ''
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), sec = s % 60
  return h > 0 ? `${h}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}` : `${m}:${String(sec).padStart(2, '0')}`
}

export default function PayShare() {
  const { token } = useParams()
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState(null)
  const [card, setCard] = useState({ number: '', expiry: '', cvc: '' })
  const [busy, setBusy] = useState(false)
  const [payErr, setPayErr] = useState(null)
  // Flipped the instant the live countdown reaches zero, so the existing expired
  // line replaces the pay form on the same second (this page doesn't poll while
  // the golfer is still unpaid, so the server `expired` flag wouldn't catch up).
  const [cdExpired, setCdExpired] = useState(false)

  function load() {
    return tournamentApi.get(`/public/quote/share/${token}`)
      .then(r => { setData(r.data); setCdExpired(false) })
      .catch(e => setErr(e?.response?.status === 404 ? 'notfound' : (e?.response?.data?.message || e.message)))
  }
  useEffect(() => { load().finally(() => setLoading(false)) }, [token]) // eslint-disable-line react-hooks/exhaustive-deps

  // Poll while waiting on the rest of the group (so this golfer sees "booked").
  useEffect(() => {
    if (!data || !data.golfer?.paid || data.split?.booked) return
    const iv = setInterval(() => { load() }, 6000)
    return () => clearInterval(iv)
  }, [data && data.golfer?.paid, data && data.split?.booked]) // eslint-disable-line react-hooks/exhaustive-deps

  async function pay() {
    const isFr = data?.quote?.language === 'fr'
    const c = { number: (card.number || '').trim(), expiry: (card.expiry || '').trim(), cvc: (card.cvc || '').trim() }
    if (!c.number || !c.expiry || !c.cvc) { setPayErr(isFr ? 'Entrez les détails de la carte.' : 'Enter your card details.'); return }
    setBusy(true); setPayErr(null)
    try {
      const r = await tournamentApi.post(`/public/quote/share/${token}/pay`, { card: c })
      if (r.data.expired) { setPayErr(isFr ? 'La garantie de prix a expiré. Contactez l\'organisateur.' : 'The price hold has expired. Please contact the trip organizer.'); await load(); return }
      setCard({ number: '', expiry: '', cvc: '' })
      await load()
    } catch (e) { setPayErr(e?.response?.data?.message || e.message) } finally { setBusy(false) }
  }

  if (loading) return <Centered><div style={{ color: '#64748b' }}>Loading…</div></Centered>
  if (err === 'notfound') return <Centered><Card><h2 style={{ margin: 0, fontSize: 20 }}>Link not available</h2><p style={{ color: '#64748b' }}>This payment link isn’t available. Please contact the trip organizer.</p></Card></Centered>
  if (err) return <Centered><Card><p style={{ color: '#991b1b' }}>{err}</p></Card></Centered>
  if (!data) return null

  const { brand, quote: q, golfer, plan, mySchedule = [], split, expired } = data
  const cur = q.currency || 'CAD'
  const fr = q.language === 'fr'
  const accent = brand?.primaryColor || '#0f172a'
  const onAccent = contrastText(accent)
  const booked = !!split?.booked
  const paid = !!golfer?.paid

  return (
    <div style={{ minHeight: '100vh', background: '#f1f5f9', padding: '0 0 40px' }}>
      {/* Brand header */}
      <div style={{ background: accent, color: onAccent, padding: '18px 16px' }}>
        <div style={{ maxWidth: 640, margin: '0 auto', display: 'flex', alignItems: 'center', gap: 12 }}>
          {brand?.logoUrl ? <img src={brand.logoUrl} alt="" style={{ height: 40, objectFit: 'contain' }} /> : <div style={{ fontSize: 18, fontWeight: 800 }}>{brand?.name}</div>}
        </div>
      </div>
      <div style={{ maxWidth: 640, margin: '0 auto', padding: '18px 16px', display: 'grid', gap: 16 }}>
        <Card>
          <div style={{ fontSize: 12, fontWeight: 700, color: accent, textTransform: 'uppercase', letterSpacing: 0.5 }}>{fr ? 'Payez votre part' : 'Pay your share'}</div>
          <h1 style={{ margin: '4px 0 2px', fontSize: 22, color: '#0f172a' }}>{q.name || (fr ? 'Voyage de golf' : 'Golf trip')}</h1>
          <div style={{ fontSize: 13.5, color: '#64748b' }}>
            {[q.destination, q.tripStartDate ? fmtDay(q.tripStartDate) : null, q.numDays ? `${q.numDays} ${fr ? 'jours' : 'days'}` : null].filter(Boolean).join(' · ')}
          </div>
          <div style={{ marginTop: 4, fontSize: 12.5, color: '#94a3b8' }}>{q.quoteNumber}</div>
          {golfer?.name && (
            <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid #f1f5f9' }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 0.5 }}>{fr ? 'Vous payez en tant que' : "You're paying as"}</div>
              <div style={{ marginTop: 2, fontSize: 15, fontWeight: 700, color: '#0f172a' }}>
                {golfer.name}{golfer.isLead ? <span style={{ fontSize: 12, fontWeight: 600, color: accent }}>{fr ? ' · organisateur' : ' · organizer'}</span> : null}
              </div>
              {golfer.email && <div style={{ marginTop: 1, fontSize: 12.5, color: '#94a3b8' }}>{golfer.email}</div>}
            </div>
          )}
        </Card>

        {/* Progress across the group */}
        {split && (
          <Card>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, fontWeight: 700, color: '#0f172a', marginBottom: 6 }}>
              <span>{fr ? 'Progression du groupe' : 'Group progress'}</span><span>{split.authorizedCount} / {split.totalGolfers} {fr ? 'payés' : 'paid'}</span>
            </div>
            <div style={{ height: 8, borderRadius: 999, background: '#e2e8f0', overflow: 'hidden' }}>
              <div style={{ height: '100%', width: (split.totalGolfers ? Math.round((split.authorizedCount / split.totalGolfers) * 100) : 0) + '%', background: accent, transition: 'width 0.3s' }} />
            </div>
          </Card>
        )}

        {/* Your share + schedule */}
        <Card style={{ border: `2px solid ${accent}` }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
            <span style={{ fontSize: 14, fontWeight: 700, color: '#0f172a' }}>{fr ? 'Votre part aujourd\'hui' : 'Your share today'}</span>
            <b style={{ fontSize: 22, color: '#0f172a' }}>{money(golfer.firstChargeShare, cur)}</b>
          </div>
          {mySchedule.length > 1 && (
            <div style={{ marginTop: 10, background: '#f8fafc', borderRadius: 10, padding: 12 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#475569', marginBottom: 4 }}>{fr ? 'Vos paiements' : 'Your payments'}</div>
              {mySchedule.map((s, i) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, padding: '2px 0' }}>
                  <span style={{ color: '#475569' }}>{i === 0 ? (fr ? "Aujourd'hui" : 'Today') : (s.when && s.when !== 'now' ? fmtFullDate(s.when, q.language) : (fr ? 'Plus tard' : 'Later'))}</span>
                  <b>{money(s.amount, cur)}</b>
                </div>
              ))}
              {plan !== 'full' && <div style={{ marginTop: 6, fontSize: 11.5, color: '#94a3b8' }}>{fr ? 'Les paiements futurs seront débités automatiquement sur votre carte à leur échéance.' : 'Future payments are automatically charged to your card on their due dates.'}</div>}
            </div>
          )}

          {booked ? (
            <div style={{ marginTop: 12, padding: '10px 12px', background: '#dcfce7', border: '1px solid #bbf7d0', borderRadius: 10, fontSize: 13.5, color: '#166534', fontWeight: 600 }}>
              ✓ {fr ? 'Le voyage est réservé — votre part est payée. Merci !' : 'The trip is booked — your share is paid. Thank you!'}
            </div>
          ) : paid ? (
            <div style={{ marginTop: 12, padding: '10px 12px', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 10, fontSize: 13.5, color: '#1e40af', lineHeight: 1.5 }}>
              {fr ? "Votre part est payée. En attente des autres golfeurs — le voyage sera réservé dès que tout le monde aura payé." : "Your share is paid. Waiting on the other golfers — the trip books as soon as everyone has paid."}
            </div>
          ) : (expired || cdExpired || split?.status === 'expired') ? (
            // Hold lapsed while collecting (expired flag), OR the hold-sweep already
            // flipped the split to 'expired' — either way it's closed, so show the
            // message up front instead of a pay form that 409s after the card is typed.
            <div style={{ marginTop: 12, padding: '10px 12px', background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: 10, fontSize: 13.5, color: '#9a3412', lineHeight: 1.5 }}>
              {fr ? "La garantie de prix a expiré. Contactez l'organisateur du voyage pour recommencer." : 'The price hold has expired. Please contact the trip organizer to start again.'}
            </div>
          ) : split?.status === 'cancelled' ? (
            <div style={{ marginTop: 12, padding: '10px 12px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 10, fontSize: 13.5, color: '#991b1b', lineHeight: 1.5 }}>
              {fr ? "Le paiement partagé de ce voyage a été annulé. Contactez l'organisateur du voyage." : 'This trip’s split payment was cancelled. Please contact the trip organizer.'}
            </div>
          ) : split && split.status && split.status !== 'collecting' ? (
            <div style={{ marginTop: 12, padding: '10px 12px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, fontSize: 13.5, color: '#475569', lineHeight: 1.5 }}>
              {fr ? "Ce paiement partagé n'est plus ouvert. Contactez l'organisateur du voyage." : 'This split is no longer open for payment. Please contact the trip organizer.'}
            </div>
          ) : (
            <>
              <SplitCountdown expiresAtMs={split?.holdExpiresAtMs} fr={fr} onExpire={() => setCdExpired(true)} />
              <div style={{ display: 'grid', gap: 8, marginTop: 12 }}>
                <input value={card.number} onChange={e => setCard(c => ({ ...c, number: e.target.value.replace(/\D/g, '').slice(0, 19).replace(/(.{4})/g, '$1 ').trim() }))}
                  inputMode="numeric" autoComplete="cc-number" placeholder={fr ? 'Numéro de carte' : 'Card number'}
                  style={{ padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 14, boxSizing: 'border-box' }} />
                <div style={{ display: 'flex', gap: 8 }}>
                  <input value={card.expiry} onChange={e => { const d = e.target.value.replace(/\D/g, '').slice(0, 4); setCard(c => ({ ...c, expiry: d.length >= 3 ? d.slice(0, 2) + ' / ' + d.slice(2) : d })) }}
                    inputMode="numeric" autoComplete="cc-exp" placeholder="MM / YY" style={{ flex: 1, padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 14, boxSizing: 'border-box' }} />
                  <input value={card.cvc} onChange={e => setCard(c => ({ ...c, cvc: e.target.value.replace(/\D/g, '').slice(0, 4) }))}
                    inputMode="numeric" autoComplete="cc-csc" placeholder="CVC" style={{ flex: 1, padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 14, boxSizing: 'border-box' }} />
                </div>
              </div>
              {payErr && <div style={{ marginTop: 10, color: '#991b1b', fontSize: 13 }}>{payErr}</div>}
              <button type="button" onClick={pay} disabled={busy}
                style={{ marginTop: 12, width: '100%', padding: '12px 16px', background: accent, color: onAccent, border: 'none', borderRadius: 8, fontSize: 15, fontWeight: 700, cursor: busy ? 'default' : 'pointer' }}>
                {busy ? (fr ? 'Traitement…' : 'Processing…') : `${fr ? 'Payer' : 'Pay'} ${money(golfer.firstChargeShare, cur)}`}
              </button>
              <div style={{ marginTop: 8, fontSize: 11, color: '#94a3b8', textAlign: 'center' }}>{fr ? 'Sécurisé par InteGolf · carte traitée via Moneris.' : 'Secured by InteGolf · card processed through Moneris.'}</div>
            </>
          )}
        </Card>
      </div>
    </div>
  )
}

function Card({ children, style }) {
  return <div style={{ background: '#fff', borderRadius: 14, padding: 18, boxShadow: '0 1px 3px rgba(0,0,0,0.08)', ...style }}>{children}</div>
}
function Centered({ children }) {
  return <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f1f5f9', padding: 16 }}><div style={{ maxWidth: 480, width: '100%' }}>{children}</div></div>
}
